/**
 * Short retrieval code, so a guest can get their clip without giving us a phone
 * number — they write six characters down and enter them later.
 *
 * Crockford base32: the digits and uppercase letters minus I, L, O and U. I/L/O
 * are the pairs people misread on a printed or hand-copied code, and U is left
 * out so a random code cannot spell something unfortunate. `normaliseShareCode`
 * maps the lookalikes back on the way in, so a guest who writes "O" and types
 * "0" — or the reverse — still lands on their clip.
 *
 * Six characters is 32^6, a shade over a billion. At event volumes a collision
 * is vanishingly unlikely, but the database column is unique and the insert
 * retries, so it is handled rather than hoped about.
 */

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const SHARE_CODE_LENGTH = 6;

export function newShareCode(): string {
  const bytes = new Uint8Array(SHARE_CODE_LENGTH);
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    // Older Android WebViews. Not security-critical — the code is a capability
    // for one clip, and the column is unique.
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }

  let out = '';
  for (let i = 0; i < SHARE_CODE_LENGTH; i += 1) {
    // 256 % 32 === 0, so a plain modulo is unbiased here.
    out += ALPHABET[(bytes[i] ?? 0) % ALPHABET.length];
  }
  return out;
}

/** Uppercase, strip separators, and fold the lookalikes. */
export function normaliseShareCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .slice(0, SHARE_CODE_LENGTH);
}

export function isValidShareCode(input: string): boolean {
  const code = normaliseShareCode(input);
  if (code.length !== SHARE_CODE_LENGTH) return false;
  return [...code].every((ch) => ALPHABET.includes(ch));
}

/** Grouped for reading aloud or copying down: ABC-123. */
export function formatShareCode(code: string): string {
  const c = normaliseShareCode(code);
  if (c.length <= 3) return c;
  return `${c.slice(0, 3)}-${c.slice(3)}`;
}
